import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from './Text'
import { GroupedAvatars, IMembers } from './Avatar'
import { ProgressBar } from './ProgressBar'
import JobTag from './tag/JobTag'
import Icon from './icon/Icon'
import { RowAligned } from './layout/LayoutAtoms'

// Utils
import { evaluationFormItems } from '../common/forms'
import { useAppState } from '../context/AppContext'
import { cultureValueColors } from '../utils/habitHelper'
import {
  getVisibleAssessors,
  getClosestStep,
  getCultureScores,
  getSkillScores,
} from '../utils/evaluationHelpers'

// Types
import { AnyColor } from '../common/models/colors'
import { ICompanyMember, ICompany } from '../common/models/Company'
import { EvaluationAssessmentType } from '../common/models/Evaluation'

interface IProps {
  variant: EvaluationAssessmentType
  membersMap: Map<number, ICompanyMember[]> // Maps each evaluation value to members who assigned it.
  skillAverage?: number
  culturePercentage?: number
  isOwnProfile: boolean
  habitColor?: string
  done?: boolean
  className?: string
  evalCompany?: ICompany
}

type ScoreExplanationProps = Omit<
  IProps,
  'membersMap' | 'isOwnProfile' | 'habitColor'
>

export const ScoreExplanation: React.FC<ScoreExplanationProps> = ({
  variant,
  skillAverage,
  culturePercentage,
  done,
  evalCompany,
  className,
}) => {
  const { theme, company } = useAppState()
  const { t } = useTranslation()
  const culturalValues =
    company &&
    getCultureScores(
      evalCompany?.settings?.evaluations?.cultureScores ? evalCompany : company
    )
  const skillValues =
    company &&
    getSkillScores(
      evalCompany?.settings?.evaluations?.skillScores ? evalCompany : company
    )

  const closestStepAverage =
    variant === 'skills' &&
    skillValues &&
    getClosestStep(skillValues, skillAverage!)
  const scoreContributors = (evalCompany || company)?.settings?.evaluations
    ?.scoreContributors

  const getText = () => {
    if (
      (variant === 'culture'
        ? culturePercentage === undefined || culturePercentage === -1
        : skillAverage === undefined || skillAverage === -1) &&
      done
    ) {
      return {
        label: t('evaluationHeader.noFinalScore'),
        hover: t('evaluationHeader.noFinalScoreExplanation'),
      }
    } else if (!done) {
      return {
        label: t('evaluationHeader.waiting'),
        hover: t('evaluationHeader.waitingExplanation'),
      }
    } else {
      return {
        label: t('evaluationHeader.finalScore'),
        hover:
          variant === 'goals'
            ? t('evaluationHeader.finalScoreExplanation')
            : t('evaluationHeader.finalScoreExplanation') +
              ': ' +
              (scoreContributors?.peers
                ? t('evaluationHeader.allPeers')
                : (scoreContributors?.leads
                    ? t('useTeamFilters.labelLeads')
                    : '') +
                  ' ' +
                  (scoreContributors?.buddies
                    ? t('general.users.buddies')
                    : '')) +
              ' ' +
              (scoreContributors?.self ? t('general.users.self') : ''),
        finalLabel:
          variant === 'goals'
            ? undefined
            : variant === 'culture'
            ? culturalValues &&
              getClosestStep(culturalValues, culturePercentage!).label
            : closestStepAverage && closestStepAverage.label,
        finalScore:
          variant === 'goals'
            ? undefined
            : variant === 'culture'
            ? `${culturePercentage}/100`
            : `${skillAverage}/100`,
      }
    }
  }

  return (
    <StyledRowAligned margin="0 0 12px" className={className}>
      <Text color="textLight" textStyle="captionHeavy">
        {`${getText().label}${getText().finalLabel ? ': ' : ''}`}
      </Text>
      {getText().finalLabel && (
        <Text color="primary" textStyle="captionHeavy" marginLeft={2}>
          {getText().finalLabel}
        </Text>
      )}
      {getText().finalScore && (
        <Text color="primaryFadeHalf" textStyle="caption" marginLeft={2}>
          ({getText().finalScore})
        </Text>
      )}
      <HoverContainer
        hoverText={getText().hover}
        content={<Icon type="Info" color={theme.colors.primary} size={16} />}
      />
    </StyledRowAligned>
  )
}

const EvaluationHeader: React.FC<IProps> = ({
  membersMap,
  isOwnProfile,
  variant,
  skillAverage,
  culturePercentage,
  habitColor,
  done,
  className,
  evalCompany,
}) => {
  const { t } = useTranslation()
  const { user, theme, company } = useAppState()
  const culturalValues =
    company &&
    getCultureScores(
      evalCompany?.settings?.evaluations?.cultureScores ? evalCompany : company
    )
  const skillValues =
    company &&
    getSkillScores(
      evalCompany?.settings?.evaluations?.skillScores ? evalCompany : company
    )
  const totalMembers = [...membersMap.values()].reduce(
    (sum, members) => sum + members.length,
    0
  )
  const customGoalsOrder = [1, 5, 4, 2, 3] // to sort them by right order
  const goalsValues = [...membersMap.entries()]
    .map(([key]) => ({
      ...evaluationFormItems.radioItems.find(({ value }) => value === key)!,
    }))
    .sort(
      (a, b) =>
        customGoalsOrder.indexOf(a.value) - customGoalsOrder.indexOf(b.value)
    )

  const closestStepAverage =
    variant === 'skills' &&
    skillValues &&
    getClosestStep(skillValues, skillAverage!)

  const {
    lightValueColor,
    darkValueColor,
    mediumValueColor,
  } = cultureValueColors(theme, habitColor!, company)

  const mapGoalsColors = (value: number): AnyColor => {
    switch (value) {
      case 1:
        return 'lightest'
      case 2:
        return 'primaryFadeSemi'
      case 3:
        return 'primary'
      case 4:
        return 'primaryFadeHalf'
      default:
        return 'primaryFadeLight'
    }
  }

  const userInEvalCompany =
    evalCompany &&
    evalCompany.allMembers.find((m) => m._id === user?.asCompanyMember?._id)

  const visibleAssessors =
    company &&
    user?.asCompanyMember &&
    getVisibleAssessors(
      evalCompany?.settings?.evaluations?.scoreVisibility
        ? evalCompany
        : company,
      userInEvalCompany || user.asCompanyMember
    )

  return (
    <Container
      className={className}
      backgroundColor={variant === 'culture' ? lightValueColor : undefined}
    >
      <ScoreExplanation
        variant={variant}
        skillAverage={skillAverage}
        culturePercentage={culturePercentage}
        done={done}
        evalCompany={evalCompany}
      />
      <ValueWrapper variant={variant}>
        {(
          (variant === 'skills'
            ? skillValues
            : variant === 'goals'
            ? goalsValues
            : culturalValues) || []
        ).map((value, index) => {
          if (!value) return null
          let members = membersMap.get(value.value)

          // for own see only your scores that are allowed in scoreVisibily settings
          if (isOwnProfile) {
            members =
              variant === 'goals'
                ? members
                : members?.filter((member) =>
                    visibleAssessors?.includes(member)
                  )
          }

          const mappedMembers: IMembers[] | undefined = members?.map(
            ({ name, picture }) => {
              return { name, avatar: picture }
            }
          )

          return (
            <ChipWithAvatar
              key={`chip-${value.label}-${index}`}
              width={
                variant === 'goals'
                  ? totalMembers > 0 && members?.length
                    ? (members.length / totalMembers) * 100
                    : 0
                  : 100
              }
            >
              <StyledGroupAvatars members={mappedMembers ?? []} />
              {variant !== 'culture' && (
                <ValueChip
                  key={index}
                  color={
                    variant === 'skills'
                      ? closestStepAverage &&
                        value.value <= closestStepAverage.value
                        ? 'primary'
                        : 'lightest'
                      : mapGoalsColors(value.value)
                  }
                />
              )}
            </ChipWithAvatar>
          )
        })}
        {variant === 'culture' && (
          <StyledProgressBar
            percentage={culturePercentage === 0 ? 10 : culturePercentage ?? 0} // if value = 0, fill the bar 10% to show not empty
            color={
              (habitColor && theme.colors[habitColor]) ??
              mediumValueColor ??
              darkValueColor
            }
          />
        )}
      </ValueWrapper>
      {variant === 'goals' && (
        <TextWrapper upperText={goalsValues.length > 1}>
          {goalsValues.map((value) => (
            <RowAligned key={value.label}>
              <Circle color={mapGoalsColors(value.value)} />

              <Text marginLeft={11} key={value.value} textStyle={'caption'}>
                {t(value.label)}
              </Text>
            </RowAligned>
          ))}
        </TextWrapper>
      )}
    </Container>
  )
}

export default EvaluationHeader

const StyledRowAligned = styled(RowAligned)`
  justify-content: center;
`

const HoverContainer = styled(JobTag)`
  padding: 0;
  border-radius: 50%;
  margin-left: 4px;
  background: ${({ theme }) => theme.colors.primaryFadeLight};
`

const Container = styled.div<{ backgroundColor?: string }>`
  position: relative;
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  background: ${({ theme, backgroundColor }) =>
    backgroundColor ?? theme.colors.primaryFadeLight};
`

export const ChipWithAvatar = styled.div<{ width?: number }>`
  width: ${({ width }) => width ?? 100}%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const StyledGroupAvatars = styled(GroupedAvatars)`
  position: absolute;
  z-index: 1;
  background-color: unset;
  box-shadow: none;

  figure {
    width: 24px;
    height: 24px;
  }
`

const TextWrapper = styled.div<{ upperText?: boolean }>`
  display: flex;
  justify-content: ${({ upperText }) =>
    upperText ? 'space-between' : 'space-evenly'};
  align-items: center;
  align-self: stretch;
`

export const ValueChip = styled.div<{ color: AnyColor }>`
  background-color: ${({ color, theme }) => theme.colors[color]};
  width: 100%;
  height: 12px;
`

export const ValueWrapper = styled.div<{ variant?: EvaluationAssessmentType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ variant }) => variant === 'skills' && '4px'};
  align-self: stretch;
  margin: 12px 0;

  ${({ variant, theme }) =>
    variant === 'goals' &&
    ` & > div {
    border-left: 1px solid ${theme.colors.darkest};
    border-right: 1px solid ${theme.colors.darkest};
  }`};

  & > *:first-child {
    border-left: none;

    & > div:nth-child(2) {
      border-radius: 24px 0 0 24px;
    }
  }

  & > *:last-child {
    border-right: none;

    & > div:nth-child(2) {
      border-radius: 0 24px 24px 0;
    }
  }

  & > *:only-child {
    & > div:nth-child(2) {
      border-radius: 24px;
    }
  }
`

const Circle = styled.div<{ color: AnyColor }>`
  border-radius: 50%;
  width: 12px;
  height: 12px;
  background-color: ${({ color, theme }) => theme.colors[color]};
`

const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  width: calc(100% - 32px);
`
